import React, { useState, useEffect, useContext, useMemo } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import DoctorMasterLayout from "../doctorlayouts/DoctorMasterLayout";
import axios from "axios";
import { AppContext } from "../../../context/AppContext";
import "../../../assets/css/doctor/doctorpages/Schedulemanagement.css";

// ── Constants ──────────────────────────────────────────────────
const DAYS = [
  { key: "monday",    label: "Monday" },
  { key: "tuesday",   label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday",  label: "Thursday" },
  { key: "friday",    label: "Friday" },
  { key: "saturday",  label: "Saturday" },
  { key: "sunday",    label: "Sunday" },
];

const SLOT_DURATIONS = [15, 20, 30, 45, 60];

const GENERATE_WEEKS_OPTIONS = [
  { value: 1, label: "1 Week" },
  { value: 2, label: "2 Weeks" },
  { value: 4, label: "1 Month" },
  { value: 8, label: "2 Months" },
  { value: 12, label: "3 Months" },
];

// Fields that define which slot-values exist for a day. Whenever one of
// these changes, previously-selected slots must be re-validated against
// the freshly generated slot list — otherwise stale picks stay in
// selected_slots and the "X of Y selected" count goes wrong even though
// the chips themselves look fine.
const SCHEDULE_FIELDS = ["start_time", "end_time", "slot_duration", "break_start", "break_end"];

const defaultDaySchedule = () => ({
  is_open: false,
  start_time: "09:00",
  end_time: "17:00",
  break_start: "",
  break_end: "",
  slot_duration: 30,
  selected_slots: [], // "HH:mm" 24-hr strings the therapist has actually picked
});

const buildDefaultSchedule = () => {
  const obj = {};
  DAYS.forEach(({ key }) => {
    obj[key] = defaultDaySchedule();
    // sensible defaults: weekdays on, weekend off
    if (key === "saturday" || key === "sunday") obj[key].is_open = false;
    else obj[key].is_open = true;
  });
  return obj;
};

// Turn "09:00" / "17:00" + duration(min) + break into the list of *possible*
// slot start-times the therapist can pick from. Each entry keeps both the
// raw 24-hr value (used as the selection key / sent to the backend) and a
// friendly 12-hr label (shown on the chip).
const generateSlotsPreview = (start, end, duration, breakStart, breakEnd) => {
  if (!start || !end || !duration) return [];
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let cursor = sh * 60 + sm;
  const endMinutes = eh * 60 + em;

  let breakStartMin = null, breakEndMin = null;
  if (breakStart && breakEnd) {
    const [bsh, bsm] = breakStart.split(":").map(Number);
    const [beh, bem] = breakEnd.split(":").map(Number);
    breakStartMin = bsh * 60 + bsm;
    breakEndMin = beh * 60 + bem;
  }

  const slots = [];
  while (cursor + Number(duration) <= endMinutes) {
    const slotEndCursor = cursor + Number(duration);

    const inBreak =
      breakStartMin !== null &&
      breakEndMin !== null &&
      cursor < breakEndMin &&
      slotEndCursor > breakStartMin;

    if (!inBreak) {
      const h = Math.floor(cursor / 60);
      const m = cursor % 60;
      const period = h >= 12 ? "PM" : "AM";
      const h12 = h % 12 === 0 ? 12 : h % 12;
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`; // 24-hr, e.g. "14:30"
      const label = `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
      slots.push({ value, label });
    }
    cursor += Number(duration);
  }
  return slots;
};

const ScheduleManagement = () => {
  const { baseUrl } = useContext(AppContext);

  const [schedule, setSchedule]         = useState(buildDefaultSchedule());
  const [activeDay, setActiveDay]       = useState("monday");
  const [generateWeeks, setGenerateWeeks] = useState(4); // default 1 month
  const [fetching, setFetching]         = useState(true);
  const [loading, setLoading]           = useState(false);
  const [message, setMessage]           = useState({ text: "", type: "" });

  // ── GET schedule ─────────────────────────────────────────────
  useEffect(() => {
    axios
      .get(`${baseUrl}/doctor/Schedule/show`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("doctor_token")}`,
          Accept: "application/json",
        },
      })
      .then((res) => {
        if (res.data && res.data.data) {
          const loaded = {};
          DAYS.forEach(({ key }) => {
            const d = res.data.data[key];
            loaded[key] = {
              is_open: !!d?.is_open,
              start_time: d?.start_time ? d.start_time.slice(0, 5) : "09:00",
              end_time: d?.end_time ? d.end_time.slice(0, 5) : "17:00",
              break_start: d?.break_start ? d.break_start.slice(0, 5) : "",
              break_end: d?.break_end ? d.break_end.slice(0, 5) : "",
              slot_duration: d?.slot_duration || 30,
              // Backend may send selected_slots with seconds ("09:00:00"),
              // just like start_time/end_time do. generateSlotsPreview()
              // produces values WITHOUT seconds ("09:00"), so without this
              // normalization the loaded selections never match the
              // generated chip values — chips show unselected while the
              // header count still shows the raw (stale-format) length.
              selected_slots: Array.isArray(d?.selected_slots)
                ? d.selected_slots.map((t) => (typeof t === "string" ? t.slice(0, 5) : t))
                : [],
            };
          });
          setSchedule(loaded);
        }
      })
      .catch((err) => console.error("Schedule load error:", err))
      .finally(() => setFetching(false));
  }, [baseUrl]);

  // Updates a single field on a day. If the field affects which slot-values
  // are even valid (timing/duration/break), previously selected slots that
  // no longer exist in the regenerated list are dropped automatically —
  // this keeps the "X of Y selected" count and the day-tab "X picked"
  // badge always in sync with what's actually shown as orange chips.
  const updateDay = (dayKey, field, value) => {
    setSchedule((prev) => {
      const updatedDay = { ...prev[dayKey], [field]: value };

      if (SCHEDULE_FIELDS.includes(field)) {
        const validValues = generateSlotsPreview(
          updatedDay.start_time,
          updatedDay.end_time,
          updatedDay.slot_duration,
          updatedDay.break_start,
          updatedDay.break_end
        ).map((s) => s.value);

        updatedDay.selected_slots = (updatedDay.selected_slots || []).filter((t) =>
          validValues.includes(t)
        );
      }

      return { ...prev, [dayKey]: updatedDay };
    });
  };

  const toggleDay = (dayKey) => {
    updateDay(dayKey, "is_open", !schedule[dayKey].is_open);
  };

  // Therapist clicks a slot chip to add/remove it from that day's picked slots
  const toggleSlotSelection = (dayKey, timeValue) => {
    setSchedule((prev) => {
      const current = prev[dayKey].selected_slots || [];
      const next = current.includes(timeValue)
        ? current.filter((t) => t !== timeValue)
        : [...current, timeValue];
      return { ...prev, [dayKey]: { ...prev[dayKey], selected_slots: next } };
    });
  };

  const selectAllForDay = (dayKey, allValues) => {
    updateDay(dayKey, "selected_slots", allValues);
  };

  const clearAllForDay = (dayKey) => {
    updateDay(dayKey, "selected_slots", []);
  };

  const copyMondayToWeekdays = () => {
    const mon = schedule.monday;
    setSchedule((prev) => {
      const next = { ...prev };
      ["tuesday", "wednesday", "thursday", "friday"].forEach((k) => {
        next[k] = { ...mon, selected_slots: [...mon.selected_slots] };
      });
      return next;
    });
    setMessage({ text: "Monday's timing and picked slots copied to Tue–Fri.", type: "success" });
  };

  // ── POST save ────────────────────────────────────────────────
  const saveSchedule = () => {
    setLoading(true);
    setMessage({ text: "", type: "" });

    // Save click hote hi page ko top pe le jao taake success/error alert
    // turant therapist ko nazar aaye.
    // NOTE: DoctorMasterLayout mein actual scrollable element window nahi,
    // balke `.page-content` hai (sidebar/header fixed hain) — isliye
    // window.scrollTo yahan kaam nahi karta, .page-content ko target karo.
    // scrollTo({ behavior: "smooth" }) kuch environments (jaise Chrome
    // DevTools mobile-emulation) mein silently fail ho jata hai — seedha
    // scrollTop set karna guaranteed aur har jagah reliable hai.
    const scrollContainer = document.querySelector(".page-content");
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    } else {
      window.scrollTo(0, 0);
    }

    const daysPayload = DAYS.map(({ key }) => {
      const day = schedule[key];

      // Re-derive the valid slot values for this day's current start/end/
      // duration/break so any stale picks (from before the therapist changed
      // the time range) never get sent to the backend.
      const validValues = day.is_open
        ? generateSlotsPreview(day.start_time, day.end_time, day.slot_duration, day.break_start, day.break_end).map((s) => s.value)
        : [];
      const cleanedSelected = (day.selected_slots || []).filter((t) => validValues.includes(t));

      return {
        day: key,
        is_open: day.is_open,
        start_time: day.is_open ? day.start_time : null,
        end_time: day.is_open ? day.end_time : null,
        break_start: day.is_open && day.break_start ? day.break_start : null,
        break_end: day.is_open && day.break_end ? day.break_end : null,
        slot_duration: Number(day.slot_duration),
        selected_slots: day.is_open ? cleanedSelected : [],
      };
    });

    const hasAnySelection = daysPayload.some((d) => d.is_open && d.selected_slots.length > 0);
    if (!hasAnySelection) {
      setMessage({
        text: "Pick at least one time slot on an open day before saving — nothing is auto-generated anymore.",
        type: "error",
      });
      setLoading(false);
      return;
    }

    axios
      .post(
        `${baseUrl}/doctor/Schedule/save`,
        { days: daysPayload, generate_weeks: Number(generateWeeks) },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("doctor_token")}`,
            Accept: "application/json",
          },
        }
      )
      .then((res) => {
        setMessage({
          text: "Schedule saved successfully!",
          type: "success",
        });
      })
      .catch((err) => {
        console.error(err);
        setMessage({
          text: err?.response?.data?.message || "Update failed. Please try again.",
          type: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  const activeCount = useMemo(
    () => DAYS.filter((d) => schedule[d.key]?.is_open).length,
    [schedule]
  );

  if (fetching) {
    return (
      <DoctorMasterLayout>
        <div className="sm-loading">Loading schedule...</div>
      </DoctorMasterLayout>
    );
  }

  const current = schedule[activeDay];
  const previewSlots = current.is_open
    ? generateSlotsPreview(current.start_time, current.end_time, current.slot_duration, current.break_start, current.break_end)
    : [];
  const selectedForActiveDay = current.selected_slots || [];

  return (
    <DoctorMasterLayout>
      <div className="schedule-management-wrapper">
        <Container>
          <Card className="sm-card">
            <div className="sm-header">
              <div>
                <h2 className="sm-title">Schedule Management</h2>
                <p className="sm-subtitle">
                  Set your weekly availability — pick exactly which time slots patients can book. Nothing is created automatically.
                </p>
              </div>
              <div className="sm-active-badge">
                {activeCount} / 7 days active
              </div>
            </div>

            {message.text && (
              <div className={`sm-alert sm-alert-${message.type}`}>
                {message.text}
              </div>
            )}

            <Row className="sm-body">
              {/* ── Day tabs ── */}
              <Col md={3} className="sm-day-list">
                {DAYS.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`sm-day-tab ${activeDay === key ? "sm-day-tab-active" : ""} ${
                      !schedule[key].is_open ? "sm-day-tab-off" : ""
                    }`}
                    onClick={() => setActiveDay(key)}
                  >
                    <span className={`sm-day-dot ${schedule[key].is_open ? "sm-dot-on" : "sm-dot-off"}`} />
                    <span className="sm-day-tab-label">{label}</span>
                    {schedule[key].is_open && (
                      <span className="sm-day-tab-time">
                        {(schedule[key].selected_slots || []).length} picked
                      </span>
                    )}
                  </button>
                ))}

                <Button variant="link" className="sm-copy-btn" onClick={copyMondayToWeekdays}>
                  ⧉ Copy Monday to Tue–Fri
                </Button>
              </Col>

              {/* ── Day editor ── */}
              <Col md={9}>
                <div className="sm-editor">
                  <div className="sm-editor-head">
                    <h4>{DAYS.find((d) => d.key === activeDay)?.label}</h4>

                    <Form.Check
                      type="switch"
                      id={`sm-switch-${activeDay}`}
                      label={current.is_open ? "Open for booking" : "Closed"}
                      checked={current.is_open}
                      onChange={() => toggleDay(activeDay)}
                      className="sm-switch"
                    />
                  </div>

                  {current.is_open ? (
                    <>
                      <Row className="mb-3">
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Working Window Start</Form.Label>
                            <Form.Control
                              type="time"
                              value={current.start_time}
                              onChange={(e) => updateDay(activeDay, "start_time", e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Working Window End</Form.Label>
                            <Form.Control
                              type="time"
                              value={current.end_time}
                              onChange={(e) => updateDay(activeDay, "end_time", e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Slot Duration</Form.Label>
                            <Form.Select
                              value={current.slot_duration}
                              onChange={(e) => updateDay(activeDay, "slot_duration", e.target.value)}
                            >
                              {SLOT_DURATIONS.map((d) => (
                                <option key={d} value={d}>{d} minutes</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Break Start (optional)</Form.Label>
                            <Form.Control
                              type="time"
                              value={current.break_start}
                              onChange={(e) => updateDay(activeDay, "break_start", e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Break End (optional)</Form.Label>
                            <Form.Control
                              type="time"
                              value={current.break_end}
                              onChange={(e) => updateDay(activeDay, "break_end", e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <div className="sm-preview-box">
                        <div className="sm-preview-label-row">
                          <span className="sm-preview-label">
                            Pick your slots — {selectedForActiveDay.length} of {previewSlots.length} selected
                          </span>
                          <span className="sm-preview-actions">
                            <button
                              type="button"
                              className="sm-preview-action-link"
                              onClick={() => selectAllForDay(activeDay, previewSlots.map((s) => s.value))}
                            >
                              Select all
                            </button>
                            <button
                              type="button"
                              className="sm-preview-action-link"
                              onClick={() => clearAllForDay(activeDay)}
                            >
                              Clear
                            </button>
                          </span>
                        </div>
                        <div className="sm-preview-slots">
                          {previewSlots.length > 0 ? (
                            previewSlots.map((s) => {
                              const isSelected = selectedForActiveDay.includes(s.value);
                              return (
                                <button
                                  type="button"
                                  key={s.value}
                                  className={`sm-preview-chip sm-preview-chip-toggle ${isSelected ? "sm-preview-chip-selected" : ""}`}
                                  onClick={() => toggleSlotSelection(activeDay, s.value)}
                                  aria-pressed={isSelected}
                                >
                                  {s.label}
                                </button>
                              );
                            })
                          ) : (
                            <span className="sm-preview-empty">
                              End time must be after start time.
                            </span>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="sm-closed-box">
                      This day is marked closed — patients won't see any slots for{" "}
                      {DAYS.find((d) => d.key === activeDay)?.label} on the booking page.
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            <div className="sm-footer">
              <div className="sm-generate-weeks">
                <Form.Label>Generate schedule for:</Form.Label>
                <Form.Select
                  value={generateWeeks}
                  onChange={(e) => setGenerateWeeks(e.target.value)}
                  style={{ width: "160px", display: "inline-block", marginLeft: "10px" }}
                >
                  {GENERATE_WEEKS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </div>
              <Button onClick={saveSchedule} disabled={loading} className="sm-save-btn">
                {loading ? "Saving..." : "Save Schedule"}
              </Button>
            </div>
          </Card>
        </Container>
      </div>
    </DoctorMasterLayout>
  );
};

export default ScheduleManagement;